/**
 * Phase 21β-2.5 — Library „Vorlagen" als kompakte Tabelle mit Split-View-Vorschau.
 *
 * Layout (100vh, kein Browser-Scroll):
 *   ┌── Topbar (Suche · KI · Neu) ─────────────────────────────────┐
 *   ├── 220px ──┬─────────── 1fr ───────────────────────────────────┤
 *   │ Kategorien│  Tabelle (Name·Kategorie·Typ·Status·Geändert·⋯)  │
 *   │  Sidebar  │  (60% Höhe, scrollt intern)                       │
 *   │  + Archiv │                                                   │
 *   │           ├───────── Split-View Vorschau (40%) ──────────────┤
 *   │           │  Betreff · Body · verwendete Platzhalter         │
 *   └───────────┴───────────────────────────────────────────────────┘
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  BookmarkSimple, MagnifyingGlass, Plus, FileText, ShieldCheck,
  Archive, ArrowCounterClockwise, DotsThreeVertical, PencilSimple,
  Copy, X, FunnelSimple, Sparkle, MagicWand,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

const TONE_FOR_CAT = {
  Mahnung: "rose", Mietverhältnis: "indigo", Nebenkosten: "amber",
  Mietanpassung: "violet", Verträge: "emerald", Schriftverkehr: "slate",
  Eigentümer: "sky", Technik: "stone", Allgemein: "slate",
};
const TONE_CSS = {
  rose: "bg-rose-50 text-rose-700",
  indigo: "bg-indigo-50 text-indigo-700",
  amber: "bg-amber-50 text-amber-800",
  violet: "bg-violet-50 text-violet-700",
  emerald: "bg-emerald-50 text-emerald-700",
  sky: "bg-sky-50 text-sky-700",
  stone: "bg-stone-100 text-stone-700",
  slate: "bg-slate-100 text-slate-700",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return "—"; }
}
function htmlToText(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
}
function macrosUsed(t) {
  const text = (t.subject || "") + " " + (t.body || "");
  const matches = text.match(/\{\{\s*([a-zA-Z0-9_.+-]+)\s*\}\}/g) || [];
  return Array.from(new Set(matches.map((m) => m.replace(/[{}\s]/g, ""))));
}

export default function DocumentTemplates() {
  const navigate = useNavigate();
  const confirmDialog = useConfirm();

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/templates", { params: { include_archived: true } });
      setItems(data || []);
      return data || [];
    } catch (e) { toast.error(formatApiError(e)); return []; } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  // categories with counts
  const categories = useMemo(() => {
    const counts = {};
    items.forEach((t) => {
      if (t.is_archived && !showArchived) return;
      const c = t.category || "Allgemein";
      counts[c] = (counts[c] || 0) + 1;
    });
    return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
  }, [items, showArchived]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((t) => {
      if (t.is_archived && !showArchived) return false;
      if (activeCat && (t.category || "Allgemein") !== activeCat) return false;
      if (!q) return true;
      const hay = [t.name, t.subject, t.category, t.description, htmlToText(t.body)].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, activeCat, showArchived]);

  useEffect(() => {
    if (filtered.length === 0) { setSelected(null); return; }
    if (!selected || !filtered.find((t) => t.id === selected.id)) setSelected(filtered[0]);
  }, [filtered]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalActive = items.filter(t => !t.is_archived || showArchived).length;
  const openEditor = (tpl) => navigate(`/vorlagen/${tpl.id}/bearbeiten`);

  const createNew = async () => {
    try {
      const { data } = await api.post("/templates", {
        name: "Neue Vorlage", category: activeCat || "Allgemein",
        subject: "Betreff …",
        sub_subject: "Ihr Mietverhältnis: {{tenancy.contract_number}}",
        body: "<p>{{recipient.greeting}},</p><p></p><p>Mit freundlichen Grüßen<br>{{letter.author_name}}</p>",
      });
      navigate(`/vorlagen/${data.id}/bearbeiten`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const duplicate = async (tpl) => {
    try {
      await api.post(`/templates/${tpl.id}/duplicate`);
      toast.success("Dupliziert"); await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const archive = async (tpl, archived) => {
    if (archived) {
      const ok = await confirmDialog({
        title: `„${tpl.name}" archivieren?`,
        description: "Kann jederzeit wiederhergestellt werden.",
        confirmText: "Archivieren", tone: "warn",
      });
      if (!ok) return;
    }
    try {
      await api.post(`/templates/${tpl.id}/archive`, { archived });
      toast.success(archived ? "Archiviert" : "Wiederhergestellt"); await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (tpl) => {
    // 2026-09 · Auch Standard-Vorlagen können vom Nutzer entfernt werden.
    // System-geseedete Vorlagen erscheinen nach dem nächsten Backend-Start
    // ggf. wieder — der Nutzer wird darauf hingewiesen.
    const isDefault = !!tpl.is_default;
    const ok = await confirmDialog({
      title: isDefault ? "Standard-Vorlage löschen?" : "Vorlage endgültig löschen?",
      description: isDefault
        ? `„${tpl.name}" ist eine System-Vorlage. Sie kann jetzt gelöscht werden — beim nächsten Backend-Neustart könnte sie automatisch wieder angelegt werden.`
        : `„${tpl.name}" wird unwiderruflich entfernt.`,
      confirmText: "Endgültig löschen", tone: "danger",
    });
    if (!ok) return;
    try { await api.delete(`/templates/${tpl.id}`); toast.success("Gelöscht"); await reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-slate-50" data-testid="document-templates-library">
      {/* Topbar */}
      <header className="px-4 lg:px-6 py-2.5 bg-white border-b border-slate-200 flex items-center gap-2 flex-wrap shrink-0">
        <BookmarkSimple weight="duotone" size={20} className="text-indigo-600 shrink-0" />
        <h1 className="text-sm font-semibold tracking-tight text-slate-900">Vorlagen</h1>
        <span className="text-[11px] text-slate-500 ml-1">{filtered.length} / {totalActive}</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative w-56">
            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Vorlagen durchsuchen…"
              className="pl-8 h-8 bg-slate-50 text-xs"
              data-testid="tpl-search"
            />
          </div>
          <Button onClick={() => setAiOpen(true)} variant="outline" size="sm"
                  className="gap-1.5 h-8 border-violet-300 text-violet-700 hover:bg-violet-50"
                  data-testid="tpl-ai-btn">
            <MagicWand size={14} /> KI
          </Button>
          <Button onClick={createNew} size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1.5 h-8" data-testid="tpl-new-btn">
            <Plus size={14} /> Neue Vorlage
          </Button>
        </div>
      </header>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[220px_1fr]">
        {/* Categories sidebar */}
        <aside className="hidden lg:flex flex-col border-r border-slate-200 bg-white overflow-y-auto" data-testid="tpl-filters">
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1 mb-1">
              <FunnelSimple size={10} /> Kategorien
            </div>
            <ul className="space-y-0.5">
              <CategoryRow active={!activeCat} count={totalActive} onClick={() => setActiveCat(null)} testid="cat-all">
                Alle Vorlagen
              </CategoryRow>
              {categories.map(([cat, count]) => (
                <CategoryRow key={cat} active={activeCat === cat} count={count}
                              onClick={() => setActiveCat(cat)} testid={`cat-${cat}`}>
                  {cat}
                </CategoryRow>
              ))}
            </ul>
          </div>
          <div className="px-3 py-2">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer text-slate-700">
              <input type="checkbox" checked={showArchived}
                     onChange={(e) => setShowArchived(e.target.checked)}
                     className="accent-slate-900 w-3 h-3"
                     data-testid="tpl-show-archived" />
              Archivierte zeigen
            </label>
          </div>
          {/* Modul-Switcher (Vertragsvorlagen kommt in nächster Iteration) */}
          <div className="mt-auto px-3 py-2 border-t border-slate-100 space-y-1">
            <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Module</div>
            <div className="text-[11px] text-slate-700 px-1.5 py-1 bg-indigo-50 rounded-sm border border-indigo-100 inline-flex items-center gap-1">
              <BookmarkSimple size={10} /> Vorlagen (aktiv)
            </div>
            <button
              disabled
              className="block w-full text-left text-[11px] text-slate-400 px-1.5 py-1 rounded-sm cursor-not-allowed"
              title="Kommt in nächster Iteration"
              data-testid="nav-vertragsvorlagen"
            >
              Vertragsvorlagen <span className="text-[9px]">(bald)</span>
            </button>
          </div>
        </aside>

        {/* Main: Table + Split-Preview */}
        <main className="flex flex-col min-h-0" data-testid="tpl-main">
          {/* Table (60% height) */}
          <div className="flex-[3] overflow-y-auto bg-white border-b border-slate-200" data-testid="tpl-table-wrap">
            <table className="w-full text-xs" data-testid="tpl-table">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  <th className="text-left px-3 py-2 w-[35%]">Name</th>
                  <th className="text-left px-2 py-2 w-[15%]">Kategorie</th>
                  <th className="text-left px-2 py-2 w-[8%]">Typ</th>
                  <th className="text-left px-2 py-2 w-[12%]">Platzhalter</th>
                  <th className="text-left px-2 py-2 w-[10%]">Status</th>
                  <th className="text-left px-2 py-2 w-[10%]">Geändert</th>
                  <th className="w-[8%] py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="text-center text-slate-500 py-8">Lade…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-slate-500 py-12">
                    <BookmarkSimple size={28} className="mx-auto text-slate-300 mb-2" />
                    Keine Vorlagen gefunden
                  </td></tr>
                )}
                {filtered.map((tpl) => {
                  const isSel = selected?.id === tpl.id;
                  const tone = TONE_CSS[TONE_FOR_CAT[tpl.category] || "slate"];
                  return (
                    <tr
                      key={tpl.id}
                      onClick={() => setSelected(tpl)}
                      onDoubleClick={() => openEditor(tpl)}
                      data-testid={`tpl-row-${tpl.slug || tpl.id}`}
                      className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                        isSel ? "bg-indigo-50/60 hover:bg-indigo-50" : ""
                      } ${tpl.is_archived ? "opacity-60" : ""}`}
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText size={13} className="text-slate-400 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-slate-900 truncate">{tpl.name}</div>
                            {tpl.subject && <div className="text-[10px] text-slate-500 truncate">{tpl.subject}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-medium ${tone}`}>
                          {tpl.category || "Allgemein"}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-[10px] text-slate-600">
                        {tpl.use_letterhead === false ? "Inhalt" : "Brief"}
                      </td>
                      <td className="px-2 py-2 text-[10px] text-slate-500 tabular-nums">
                        {macrosUsed(tpl).length}
                      </td>
                      <td className="px-2 py-2">
                        {tpl.is_archived ? (
                          <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                            <Archive size={9} /> Archiv
                          </span>
                        ) : tpl.is_default ? (
                          <span className="text-[10px] text-emerald-700 inline-flex items-center gap-0.5">
                            <ShieldCheck size={9} /> Standard
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-500">aktiv</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-[10px] text-slate-500 tabular-nums">
                        {fmtDate(tpl.updated_at || tpl.created_at)}
                      </td>
                      <td onClick={(e) => e.stopPropagation()} className="text-right pr-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="text-slate-400 hover:text-slate-700 p-1 rounded-sm" data-testid={`tpl-menu-${tpl.id}`}>
                              <DotsThreeVertical size={14} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem onClick={() => openEditor(tpl)} data-testid={`tpl-edit-${tpl.id}`}>
                              <PencilSimple size={13} className="mr-2" /> Bearbeiten
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => duplicate(tpl)} data-testid={`tpl-duplicate-${tpl.id}`}>
                              <Copy size={13} className="mr-2" /> Duplizieren
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {tpl.is_archived ? (
                              <DropdownMenuItem onClick={() => archive(tpl, false)} data-testid={`tpl-restore-${tpl.id}`}>
                                <ArrowCounterClockwise size={13} className="mr-2" /> Wiederherstellen
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => archive(tpl, true)} data-testid={`tpl-archive-${tpl.id}`}>
                                <Archive size={13} className="mr-2" /> Archivieren
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => remove(tpl)} className="text-rose-700" data-testid={`tpl-delete-${tpl.id}`}>
                              <X size={13} className="mr-2" /> Löschen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Split-View Preview (40% height) */}
          <div className="flex-[2] overflow-hidden bg-white grid grid-cols-1 lg:grid-cols-[1fr_280px]" data-testid="tpl-preview-pane">
            {selected ? (
              <>
                <div className="overflow-y-auto p-4 border-r border-slate-100">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Vorschau</div>
                      <div className="text-sm font-semibold text-slate-900 truncate">{selected.name}</div>
                    </div>
                    <Button size="sm" onClick={() => openEditor(selected)} className="bg-slate-900 hover:bg-slate-800 h-7 gap-1 text-xs" data-testid="tpl-open-editor">
                      <PencilSimple size={12} /> Öffnen
                    </Button>
                  </div>
                  {selected.subject && (
                    <>
                      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">Betreff</div>
                      <div className="text-xs font-medium text-slate-800 mb-2 break-words">{selected.subject}</div>
                    </>
                  )}
                  <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">Inhalt</div>
                  <div className="tiptap-doc text-xs"
                       dangerouslySetInnerHTML={{ __html: selected.body || "<em>Leer</em>" }} />
                </div>
                <div className="hidden lg:block overflow-y-auto p-3 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Eigenschaften</div>
                  <DetailRow label="Kategorie" value={selected.category || "—"} />
                  <DetailRow label="Typ" value={selected.use_letterhead === false ? "Inhalt (ohne Briefpapier)" : "Brief (mit Briefpapier)"} />
                  <DetailRow label="Standard" value={selected.is_default ? "Ja" : "Nein"} />
                  <DetailRow label="Schriftart" value={selected.font_family || "Calibri"} />
                  <DetailRow label="Schriftgröße" value={`${selected.font_size_pt || 11} pt`} />
                  <DetailRow label="Geändert" value={fmtDate(selected.updated_at || selected.created_at)} />
                  {selected.description && (
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Notiz</div>
                      <div className="text-[11px] text-slate-600 italic">{selected.description}</div>
                    </div>
                  )}
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 inline-flex items-center gap-1">
                      <Sparkle size={9} /> Platzhalter ({macrosUsed(selected).length})
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {macrosUsed(selected).map((m) => (
                        <span key={m} className="text-[9px] font-mono text-slate-600 bg-white border border-slate-200 px-1 py-0.5 rounded-sm">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="lg:col-span-2 flex items-center justify-center text-slate-400 text-xs">
                <div className="text-center">
                  <FileText size={24} className="mx-auto text-slate-300 mb-1" />
                  Wähle eine Vorlage aus, um die Vorschau zu sehen.
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {aiOpen && (
        <AiGenerateDialog
          onClose={() => setAiOpen(false)}
          onCreated={async (newId) => { setAiOpen(false); await reload(); navigate(`/vorlagen/${newId}/bearbeiten`); }}
        />
      )}
    </div>
  );
}

function CategoryRow({ active, onClick, children, count, testid }) {
  return (
    <li>
      <button
        onClick={onClick}
        data-testid={testid}
        className={`w-full flex items-center justify-between px-2 py-1 rounded-sm text-xs transition-colors ${
          active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
        }`}
      >
        <span className="truncate">{children}</span>
        <span className={`text-[9px] tabular-nums ${active ? "text-white/70" : "text-slate-400"}`}>{count}</span>
      </button>
    </li>
  );
}
function DetailRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-[10px] text-slate-500">{label}</span>
      <span className="text-[11px] text-slate-800 text-right truncate">{value}</span>
    </div>
  );
}

function AiGenerateDialog({ onClose, onCreated }) {
  const [prompt, setPrompt] = useState("");
  const [category, setCategory] = useState("Allgemein");
  const [tone, setTone] = useState("formell");
  const [working, setWorking] = useState(false);

  const submit = async () => {
    if (!prompt.trim()) { toast.error("Bitte Beschreibung eingeben"); return; }
    setWorking(true);
    try {
      const { data } = await api.post("/templates/ai/generate", { prompt, category, tone });
      // Save as new template
      const { data: created } = await api.post("/templates", {
        name: prompt.slice(0, 60) + (prompt.length > 60 ? "…" : ""),
        category: data.category || category,
        subject: data.subject || "",
        body: data.body || "<p></p>",
        description: `Mit KI erzeugt: „${prompt.slice(0, 80)}"`,
        is_html: true,
      });
      toast.success("Vorlage von KI erstellt");
      onCreated(created.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setWorking(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="ai-generate-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <MagicWand size={20} className="text-violet-600" /> Vorlage mit KI erstellen
          </DialogTitle>
          <DialogDescription>
            Beschreibe in eigenen Worten, was die Vorlage leisten soll. Die KI nutzt automatisch die passenden Platzhalter.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Beschreibung</Label>
            <textarea
              value={prompt} onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              placeholder="z. B. „Erstelle eine zweite Mahnung mit 7-tägiger Zahlungsfrist und Hinweis auf Inkasso"
              className="w-full mt-1 border border-slate-300 rounded-sm p-2 text-sm resize-none"
              data-testid="ai-prompt"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Kategorie</Label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                      className="w-full h-9 mt-1 border border-slate-300 rounded-sm px-2 text-sm bg-white"
                      data-testid="ai-category">
                {["Allgemein","Mahnung","Mietverhältnis","Nebenkosten","Mietanpassung","Verträge","Schriftverkehr"].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Tonalität</Label>
              <select value={tone} onChange={(e) => setTone(e.target.value)}
                      className="w-full h-9 mt-1 border border-slate-300 rounded-sm px-2 text-sm bg-white"
                      data-testid="ai-tone">
                <option value="formell">Formell</option>
                <option value="freundlich">Freundlich</option>
                <option value="neutral">Neutral</option>
                <option value="streng">Streng</option>
              </select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={working}>Abbrechen</Button>
          <Button onClick={submit} disabled={working} className="bg-violet-600 hover:bg-violet-700 gap-1.5" data-testid="ai-generate-submit">
            <MagicWand size={14} /> {working ? "KI generiert…" : "Generieren & öffnen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
